import React, { useState } from 'react';
import { Button, Form, Modal, Row, Select, Space, Table } from 'antd';
import { observer } from 'mobx-react-lite';
import { useForm } from 'antd/es/form/Form';
import { toJS } from 'mobx';
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import LoanLayout from '../LoanLayout';
import { useLoan } from '../../stores/store';
import Loading from '../Loading';
import ButtonNav from '../../components/ButtonNav/ButtonNav';
import { stepFinished } from '../../nav';
import PlaceholderLabel from '../../components/PlaceholderLabel/PlaceholderLabel';
import MoneyInput from '../../components/MoneyInput/MoneyInput';
import { formatCurrency } from '../../utils';
import { createSchemaValidator } from '../../validators/schemaValidator';

import styles from './financial.module.scss';
import { pickFields } from './meta';
import AppFormItem from '../components/AppFormItem';
import { Borrower, LoanPurpose, RealEstate, RealEstateName } from '../../schema';
import { ReactComponent as SelectArrowIcon } from '../../icons/select-arrow-icon.svg';
import { StepFooter } from '../components/StepFooter/StepFooter';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { FF_FULL_APPLICATION_STYLING } from '../../constants';

const formItemLayout = {
  labelCol: { span: 0 },
  wrapperCol: { span: 24 },
};

type Props = {};

let editingCol: number | null = null;
const schemaValidator = createSchemaValidator(pickFields('real-estate'));

const RealEstateStep: React.FC<Props> = observer(function RealEstateStep() {
  const loanStore = useLoan();
  const { loan } = loanStore;
  const [form] = useForm();
  const [showAdd, setShowAdd] = useState(false);
  const [modalAsset, setModalAsset] = useState<Partial<RealEstate>>({});
  const isNewFunnel = useFeatureFlag({
    flag: FF_FULL_APPLICATION_STYLING,
  });

  if (!loan || loanStore.status === 'pending') {
    return <Loading />;
  }
  const borrower = loan.borrower as Borrower;
  const statement = borrower.financialStatement;
  if (!statement || !statement.realEstate) {
    loanStore.setupRealEstate(borrower);
    return null;
  }

  const handleSubmitRealEstate = () => {
    loanStore.addRealEstate(borrower, modalAsset as RealEstate, editingCol);
    // noinspection JSIgnoredPromiseFromCall
    loanStore.syncDoc();
    setShowAdd(false);
    form.resetFields();
    editingCol = null;
  };

  const handleCancel = () => {
    setShowAdd(false);
    form.resetFields();
    editingCol = null;
    return false;
  };

  const columns: ColumnsType<RealEstate> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      render: (text: any) => (text ? formatCurrency(text) : ''),
    },
    {
      title: 'Balance',
      dataIndex: 'balance',
      key: 'balance',
      render: (text: any) => (text ? formatCurrency(text) : ''),
    },
    {
      title: 'Payment',
      dataIndex: 'payment',
      key: 'payment',
      render: (text: any) => (text ? formatCurrency(text) : ''),
    },
    {
      title: 'Second Balance',
      dataIndex: 'balance2',
      key: 'balance2',
      render: (text: any) => (text ? formatCurrency(text) : ''),
    },
    {
      title: 'Second Payment',
      dataIndex: 'payment2',
      key: 'payment2',
      render: (text: any) => (text ? formatCurrency(text) : ''),
    },
    {
      title: 'Actions',
      key: 'action',
      fixed: 'right',
      render: (text: any, record: RealEstate, index: number) => (
        <Space size="middle">
          <Button
            onClick={() => {
              editingCol = index;
              form.setFieldsValue(record);
              setModalAsset({});
              setShowAdd(true);
            }}
          >
            <EditOutlined />
          </Button>
          <Button
            onClick={() => {
              loanStore.removeRealEstate(borrower, index);
            }}
          >
            <DeleteOutlined />
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <LoanLayout className="assetsLayout">
      {isNewFunnel && <div className="step-title">About You</div>}
      <h1>
        {loan?.purpose === LoanPurpose.boat
          ? 'Tell us about any real estate you own'
          : 'Real Estate'}
      </h1>
      {loan?.purpose === LoanPurpose.boat && <p>Add your real estate assets below (optional).</p>}

      {loan?.purpose !== LoanPurpose.boat && (
        <p className="assetsSubtitle">Please tell us about any real estate you own.</p>
      )}

      <Table
        dataSource={toJS(statement.realEstate)}
        columns={columns}
        pagination={false}
        scroll={{ x: 1200 }}
      />

      <Modal title="Add Real Estate" visible={showAdd} footer={false} onCancel={handleCancel}>
        <Form form={form} {...formItemLayout} onFinish={handleSubmitRealEstate}>
          <AppFormItem
            name="name"
            required
            rules={[
              schemaValidator('name', modalAsset, {
                message: 'Please enter a type of account or debt',
              }),
            ]}
          >
            <PlaceholderLabel label="Type of Real Estate">
              <Select
                suffixIcon={<SelectArrowIcon />}
                showSearch={Object.keys(RealEstateName).length > 7}
                allowClear
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {Object.entries(RealEstateName).map(([key, label]) => (
                  <Select.Option key={key} value={label}>
                    {label}
                  </Select.Option>
                ))}
              </Select>
            </PlaceholderLabel>
          </AppFormItem>

          <AppFormItem
            name="value"
            required
            rules={[
              schemaValidator('value', modalAsset, {
                message: 'Please enter the value',
                transform: 'float',
              }),
            ]}
          >
            <PlaceholderLabel label="Value">
              <MoneyInput />
            </PlaceholderLabel>
          </AppFormItem>

          <AppFormItem
            name="balance"
            rules={[
              schemaValidator('balance', modalAsset, {
                transform: 'float',
              }),
            ]}
          >
            <PlaceholderLabel label="Remaining First Mortgage Amount (if applicable)">
              <MoneyInput />
            </PlaceholderLabel>
          </AppFormItem>

          <AppFormItem
            name="payment"
            rules={[
              schemaValidator('payment', modalAsset, {
                transform: 'float',
              }),
            ]}
          >
            <PlaceholderLabel label="Monthly Payment (if applicable)">
              <MoneyInput />
            </PlaceholderLabel>
          </AppFormItem>

          <AppFormItem
            name="balance2"
            rules={[
              schemaValidator('balance2', modalAsset, {
                transform: 'float',
              }),
            ]}
          >
            <PlaceholderLabel label="Remaining Second Mortgage Amount (if applicable)">
              <MoneyInput />
            </PlaceholderLabel>
          </AppFormItem>

          <AppFormItem
            name="payment2"
            rules={[
              schemaValidator('payment2', modalAsset, {
                transform: 'float',
              }),
            ]}
          >
            <PlaceholderLabel label="Monthly Payment (if applicable)">
              <MoneyInput />
            </PlaceholderLabel>
          </AppFormItem>

          <Row justify="end">
            <Button name="cancel" type="default" htmlType="button" onClick={handleCancel}>
              Cancel
            </Button>

            <Button name="ok" className={styles.addButton} type="primary" htmlType="submit">
              Okay
            </Button>
          </Row>
        </Form>
      </Modal>

      <Row className={styles.tableActionRow}>
        <Button
          name="add"
          type="default"
          htmlType="button"
          onClick={() => {
            setShowAdd(true);
            return false;
          }}
        >
          <PlusOutlined />
          Add Real Estate
        </Button>
      </Row>

      <ButtonNav onNext={stepFinished} />
      <StepFooter type="secure" />
    </LoanLayout>
  );
});

export default RealEstateStep;
